import React, {
  useState,
} from 'react';

import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import {
  DashboardOutlined,
  LocalShippingOutlined,
  LogoutOutlined,
  PersonOutlined,
  ReceiptLongOutlined,
  StorefrontOutlined,
} from '@mui/icons-material';

import {
  useNavigate,
} from 'react-router-dom';

import useAuth from '../../hooks/useAuth';

import {
  useSendLogoutMutation,
} from '../../features/auth/authApiSlice';

import {
  tokens,
} from '../../theme';


const AccountMenu = ({
  dashboardSide = false,
}) => {
  const theme =
    useTheme();

  const colors =
    tokens(
      theme.palette.mode
    );

  const navigate =
    useNavigate();

  const {
    username,
    status,
    organization_id,
  } = useAuth();


  const [
    anchorEl,
    setAnchorEl,
  ] = useState(null);


  const [
    sendLogout,
    {
      isLoading:
      isLoggingOut,
    },
  ] =
    useSendLogoutMutation();


  const open =
    Boolean(
      anchorEl
    );


  const closeMenu =
    () => {
      setAnchorEl(
        null
      );
    };


  const navigateTo =
    (path) => {
      closeMenu();

      navigate(
        path
      );
    };


  const handleProfile =
    () => {
      if (
        !username
      ) {
        return;
      }

      navigateTo(
        `/console/users/${encodeURIComponent(
          username
        )}`
      );
    };


  const handleLogout =
    async () => {
      if (
        isLoggingOut
      ) {
        return;
      }

      closeMenu();

      try {
        await sendLogout()
          .unwrap();

        navigate(
          '/',
          {
            replace:
              true,
          }
        );
      } catch (error) {
        console.error(
          'Logout failed:',
          error
        );
      }
    };


  const avatarLetter =
    String(
      username ||
      'U'
    )
      .charAt(0)
      .toUpperCase();


  const isAdmin =
    status === 'Admin' ||
    organization_id === 'admin';


  return (
    <>
      <Tooltip
        title="Account"
      >
        <IconButton
          onClick={
            (event) =>
              setAnchorEl(
                event.currentTarget
              )
          }

          sx={{
            p:
              0.5,
          }}
        >
          <Avatar
            sx={{
              width:
                38,

              height:
                38,

              fontSize:
                '1rem',

              fontWeight:
                800,

              bgcolor:
                colors.greenAccent[600],

              color:
                colors.grey[100],

              border:
                `1px solid ${colors.greenAccent[400]}`,
            }}
          >
            {avatarLetter}
          </Avatar>
        </IconButton>
      </Tooltip>


      <Menu
        anchorEl={
          anchorEl
        }

        open={
          open
        }

        onClose={
          closeMenu
        }

        transformOrigin={{
          horizontal:
            'right',

          vertical:
            'top',
        }}

        anchorOrigin={{
          horizontal:
            'right',

          vertical:
            'bottom',
        }}

        slotProps={{
          paper: {
            elevation:
              8,

            sx: {
              mt:
                1,

              width:
                310,

              maxWidth:
                'calc(100vw - 24px)',

              borderRadius:
                '14px',

              backgroundColor:
                colors.primary[400],

              color:
                colors.grey[100],

              border:
                `1px solid ${colors.grey[700]}`,

              boxShadow:
                '0 12px 36px rgba(0,0,0,0.32)',

              '& .MuiMenuItem-root': {
                minHeight:
                  52,

                px:
                  2,

                mx:
                  1,

                my:
                  0.25,

                borderRadius:
                  '8px',
              },
            },
          },
        }}
      >
        {/* USER INFO */}

        <Box
          sx={{
            display:
              'flex',

            alignItems:
              'center',

            gap:
              1.5,

            px:
              2,

            pt:
              1.5,

            pb:
              2,
          }}
        >
          <Avatar
            sx={{
              width:
                48,

              height:
                48,

              bgcolor:
                colors.greenAccent[600],

              color:
                colors.grey[100],

              fontWeight:
                900,
            }}
          >
            {avatarLetter}
          </Avatar>

          <Box
            sx={{
              minWidth:
                0,
            }}
          >
            <Typography
              sx={{
                fontWeight:
                  900,

                overflow:
                  'hidden',

                textOverflow:
                  'ellipsis',

                whiteSpace:
                  'nowrap',
              }}
            >
              {username ||
                'USFuel User'}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color:
                  colors.greenAccent[400],

                fontWeight:
                  700,
              }}
            >
              {status
                ? `ATG Console ${status}`
                : 'ATG Console'}
            </Typography>

            {organization_id && (
              <Typography
                variant="caption"
                sx={{
                  color:
                    colors.grey[300],
                }}
              >
                {isAdmin
                  ? 'USFuel Administration'
                  : `Organization ${organization_id}`}
              </Typography>
            )}
          </Box>
        </Box>


        <Divider />


        {/* PROFILE */}

        <MenuItem
          onClick={
            handleProfile
          }
        >
          <ListItemIcon>
            <PersonOutlined
              fontSize="small"
            />
          </ListItemIcon>

          <Box>
            <Typography
              fontWeight={
                700
              }
            >
              View Profile
            </Typography>

            <Typography
              variant="caption"
            >
              Account details and preferences
            </Typography>
          </Box>
        </MenuItem>


        {/* MY ORDERS */}

        <MenuItem
          onClick={
            () =>
              navigateTo(
                '/orders'
              )
          }
        >
          <ListItemIcon>
            <ReceiptLongOutlined
              fontSize="small"
            />
          </ListItemIcon>

          <Box>
            <Typography
              fontWeight={
                700
              }
            >
              My Orders
            </Typography>

            <Typography
              variant="caption"
            >
              Purchases and tracking
            </Typography>
          </Box>
        </MenuItem>


        {/* STORE / ATG CONSOLE */}

        {dashboardSide ? (
          <MenuItem
            onClick={
              () =>
                navigateTo(
                  '/store'
                )
            }
          >
            <ListItemIcon>
              <StorefrontOutlined
                fontSize="small"
              />
            </ListItemIcon>

            <Box>
              <Typography
                fontWeight={
                  700
                }
              >
                USFuel Store
              </Typography>

              <Typography
                variant="caption"
              >
                Browse products and equipment
              </Typography>
            </Box>
          </MenuItem>
        ) : (
          <MenuItem
            onClick={
              () =>
                navigateTo(
                  '/console'
                )
            }
          >
            <ListItemIcon>
              <DashboardOutlined
                fontSize="small"
              />
            </ListItemIcon>

            <Box>
              <Typography
                fontWeight={
                  700
                }
              >
                ATG Console
              </Typography>

              <Typography
                variant="caption"
              >
                Open your dashboard
              </Typography>
            </Box>
          </MenuItem>
        )}


        {/* ADMIN ORDER MANAGEMENT */}

        {isAdmin && (
          <MenuItem
            onClick={
              () =>
                navigateTo(
                  '/console/orders'
                )
            }
          >
            <ListItemIcon>
              <LocalShippingOutlined
                fontSize="small"
              />
            </ListItemIcon>

            <Box>
              <Typography
                fontWeight={
                  700
                }
              >
                Manage Orders
              </Typography>

              <Typography
                variant="caption"
              >
                Fulfillment and shipping
              </Typography>
            </Box>
          </MenuItem>
        )}


        <Divider
          sx={{
            my:
              0.75,
          }}
        />


        {/* LOGOUT */}

        <MenuItem
          onClick={
            handleLogout
          }

          disabled={
            isLoggingOut
          }

          sx={{
            color:
              colors.redAccent[400],
          }}
        >
          <ListItemIcon>
            {isLoggingOut ? (
              <CircularProgress
                size={
                  20
                }
              />
            ) : (
              <LogoutOutlined
                fontSize="small"
                sx={{
                  color:
                    colors.redAccent[400],
                }}
              />
            )}
          </ListItemIcon>

          <Typography
            fontWeight={
              800
            }
          >
            {isLoggingOut
              ? 'Signing out...'
              : 'Sign Out'}
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );
};


export default AccountMenu;